import React from "react";
import { ChevronLeft } from "lucide-react";
import { Wordmark } from "@/components/Brand";
import { StepProgress } from "@/components/StepProgress";
import { useBooking } from "@/context/BookingContext";

export function BookingShell({ children, footer }) {
    const { step, back } = useBooking();
    const canGoBack = step !== "category" && step !== "success";

    return (
        <div className="relative min-h-[100dvh] flex flex-col">
            {/* Sticky header */}
            <header
                className="sticky top-0 z-30 glass-strong"
                style={{ paddingTop: "env(safe-area-inset-top)" }}
                data-testid="booking-header"
            >
                <div className="max-w-xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={back}
                        aria-label="Back"
                        className={`h-10 w-10 -ml-2 rounded-full flex items-center justify-center transition-all ${
                            canGoBack
                                ? "text-white/85 hover:bg-white/5 active:scale-95"
                                : "opacity-0 pointer-events-none"
                        }`}
                        data-testid="header-back-button"
                    >
                        <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
                    </button>

                    <Wordmark small />

                    <div className="h-10 w-10" />
                </div>
                <div className="max-w-xl mx-auto px-5 pb-3">
                    <StepProgress />
                </div>
            </header>

            {/* Content */}
            <main className="flex-1 relative">
                <div className="max-w-xl mx-auto px-5 pt-6 pb-40">{children}</div>
            </main>

            {/* Sticky footer (CTA) */}
            {footer && (
                <div
                    className="fixed bottom-0 inset-x-0 z-30 glass-strong border-t hairline"
                    style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                    data-testid="booking-footer"
                >
                    <div className="max-w-xl mx-auto px-5 py-4">{footer}</div>
                </div>
            )}
        </div>
    );
}

export function PrimaryButton({ children, disabled, onClick, testId, type = "button" }) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className="w-full h-14 rounded-full bg-gold text-navy font-ui font-semibold text-base tracking-wide
                shadow-[0_10px_30px_-8px_rgba(197,160,89,0.55)]
                hover:bg-gold-light active:scale-[0.98] transition-all
                disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
        >
            {children}
        </button>
    );
}

export function StepTitle({ eyebrow, title, subtitle, testId }) {
    return (
        <div className="mb-8 animate-fade-up" data-testid={testId}>
            {eyebrow && (
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold/80 mb-3">
                    {eyebrow}
                </div>
            )}
            <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-white">
                {title}
            </h1>
            {subtitle && (
                <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-md">{subtitle}</p>
            )}
        </div>
    );
}

export function ErrorState({ error, onRetry, title = "We couldn't reach the booking system.", label = "Connection issue" }) {
    // In the staff portal (.app-light) this renders as a navy highlight card
    // matching the mockup; in the public booking flow it inherits the glass
    // dark treatment via CSS overrides.
    return (
        <div className="mt-6 rounded-2xl surface-highlight app-light:surface-highlight glass p-5 md:p-6 flex items-start gap-4 animate-fade-up" data-testid="error-state">
            <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center shrink-0">
                <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-gold" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-gold text-[10px] uppercase tracking-[0.24em] font-semibold">{label}</div>
                <div className="font-serif text-xl md:text-2xl text-white leading-tight mt-1">{title}</div>
                <p className="mt-1 text-sm text-white/70">{error?.message || "The booking system is not responding right now. Please try again shortly."}</p>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="mt-3 rounded-full border border-gold/50 px-4 py-1.5 text-xs text-gold font-semibold hover:bg-gold/10 transition-colors"
                        data-testid="error-retry-button"
                    >
                        Try again
                    </button>
                )}
            </div>
        </div>
    );
}

export function Skeleton({ className = "" }) {
    return (
        <div
            className={`rounded-2xl bg-gradient-to-r from-white/[0.03] via-white/[0.07] to-white/[0.03] bg-[length:200%_100%] animate-shimmer ${className}`}
        />
    );
}
